<script setup lang="ts">
import { defineProps, defineEmits } from "vue";
import FocusModeNoteModal from "./FocusModeNoteModal.vue";
import { useTranslate } from "@/composables/useTranslate";
import { FocusImage } from "@/types";

const props = defineProps<Props>();
type Props = {
  image: FocusImage | null;
  isFocusModeNoteModalOpen: boolean;
};

const emits = defineEmits<Emits>();
type Emits = {
  (event: "onAddImageComment", comment: string, logId: number): void;
  (event: "onDeleteImageComment"): void;
  (event: "onNoteModalToggle"): void;
  (event: "onNextImage"): void;
  (event: "onPreviousImage"): void;
};

const { t } = useTranslate();
</script>

<template>
  <Transition name="fade">
    <FocusModeNoteModal
      v-if="isFocusModeNoteModalOpen"
      :is-modal-open="isFocusModeNoteModalOpen"
      :image="image"
      @on-close="emits('onNoteModalToggle')"
      @on-add-image-comment="
      (comment:string, logId:number) => emits('onAddImageComment', comment, logId)
    "
      @on-delete-image-comment="emits('onDeleteImageComment')"
    />
  </Transition>

  <div class="w-full h-full flex flex-col overflow-y-auto">
    <!-- Image -->
    <div class="relative w-full h-[calc(100vh-233px)]">
      <img
        class="w-full h-full object-contain rounded-primary-radius"
        :src="image?.imageUrl"
        alt="Image"
      />
      <div
        @click="emits('onPreviousImage')"
        class="absolute z-10 top-0 left-0 bottom-0 w-1/4 h-full bg-transparent cursor-pointer"
      ></div>
      <div
        @click="emits('onNextImage')"
        class="absolute z-10 top-0 right-0 bottom-0 w-1/4 h-full transparent cursor-pointer"
      ></div>
    </div>
    <!-- Footer -->
    <div class="w-full h-[92px] flex flex-col sm:flex-row gap-2 mt-2">
      <!-- Info block -->
      <div
        class="min-w-[208px] max-w-[208px] py-1 px-2 bg-[#F9F8F6] rounded-primary-radius"
      >
        <h4
          class="text-[#393840] text-base leading-[26px] tracking-[0.01rem] font-medium pb-1"
        >
          {{ t("focus_mode_modal.more_info") }}
        </h4>
        <div>
          <div class="flex items-center gap-1 mb-[2px]">
            <img src="@/assets/focus-modal-calendar.svg" alt="Calendar" />
            <span class="text-[#616068] text-sm leading-[20px] font-normal">{{
              image?.createdAt
                ? new Date(image?.createdAt).toLocaleDateString("DE")
                : ""
            }}</span>
          </div>
          <div class="h-[20px] overflow-y-auto flex items-start gap-1">
            <img src="@/assets/focus-modal-user-icon.svg" alt="User" />
            <div class="flex flex-wrap items-center gap-1">
              <span
                v-for="worker in image?.workers"
                :key="worker.id"
                class="text-[#616068] text-sm leading-[20px] font-normal"
                >{{ worker.fullname }}</span
              >
            </div>
          </div>
        </div>
      </div>
      <!-- Note block -->
      <div
        @click="emits('onNoteModalToggle')"
        class="w-full h-[92px] py-1 px-2 bg-[#F9F8F6] rounded-primary-radius cursor-pointer"
      >
        <div class="w-full flex items-center justify-between">
          <h4
            class="text-[#393840] text-base leading-[26px] tracking-[0.01rem] font-medium"
          >
            {{ t("focus_mode_modal.note") }}
          </h4>
          <button class="flex items-center justify-center">
            <img src="@/assets/focus-mode-edit-pencil.svg" alt="Pencil" />
          </button>
        </div>
        <div v-if="!image?.comment">
          <span class="text-[#616068] text-sm leading-5 font-normal">{{
            t("focus_mode_modal.no_note")
          }}</span>
        </div>
        <div class="h-[55px] overflow-y-auto" v-else>
          <span class="text-[#616068] text-sm leading-5 font-normal">{{
            image?.comment
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
